import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import styles from "../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss";
import entStyles from "../../styles/enterprise/EnterpriseOperationalUx.module.scss";
import Compliance from "../Compliance";
import { getStandardDisplayName, normalizeComplianceScope, normalizeComplianceStandardCode, withComplianceScopeQuery } from "../../utils/complianceStandard";
import { getActiveOrgId } from "../../utils/orgUtils";
import { fetchEffectiveStandardAccess, type EffectiveStandardAccess } from "../../services/standardAccessApi";
import {
  buildStandardsSelectorOptions,
  isStandardAccessPolicyV1Enabled,
  isStandardGrantedInEffective,
  NO_STANDARDS_GRANTED_MESSAGE_KEY,
} from "../../utils/standardAccessPolicy";
import OperationalWorkflowButton from "../../components/compliance-workflow/OperationalWorkflowButton";
import { Button } from "../../components/ui/Button";
import { executiveGovernanceStandardPath } from "../../services/executiveGovernanceApi";
import ExecutiveStandardsOverviewBlock from "../../components/executive-governance/ExecutiveStandardsOverviewBlock";
import EnterpriseOperationalOverviewPanel from "../../components/enterprise-operational/EnterpriseOperationalOverviewPanel";
import EnterpriseOperationalPage from "../../components/enterprise-operational/EnterpriseOperationalPage";
import EnterpriseOperationalSection, {
  EnterpriseOperationalCard,
} from "../../components/enterprise-operational/EnterpriseOperationalSection";
import { Select } from "../../components/ui/Select";
import { useComplianceExecution } from "../../context/ComplianceExecutionContext";
import HomeExecutiveGovernanceIntelligence from "../../components/home/HomeExecutiveGovernanceIntelligence";
import ComplianceCenterStandardExecutiveCards, {
  frameworkMatchesCode,
} from "../../components/compliance-center/ComplianceCenterStandardExecutiveCards";
import { metricsApi } from "../../services/api";
import type { ExecutiveSummaryData } from "../../components/home/HomeExecutiveKpiPanels";

type OverviewData = {
  overallCompliance: number;
  verifiedScore: number;
  verifiedAssessmentStatus: string;
  certificationStatus: string;
  readinessStatus: string;
  auditReadinessScore: number;
  activeStandards: number;
  assessedStandards: number;
  standardsWithCriticalGaps: number;
  standardsReadyForAudit: number;
  totalEvidence: number;
  verifiedEvidence: number;
  staleEvidence: number;
  missingEvidence: number;
  openCriticalRecommendations: number;
  overdueRemediationTasks: number;
  blockedControls: number;
  plannedAudits: number;
  remediationVelocity: number;
  activeCertificationWorkflows: number;
  packagesReady: number;
  underAuditorReview: number;
  openDriftEvents: number;
  automationRulesEnabled: number;
  failedAutomationRuns: number;
  automatedEvidenceRequests: number;
  aiProviderStatus: string;
  recentAiRecommendations: number;
  executiveRiskPosture: string;
  topBlockers: number;
  nextBestActions: number;
  evidenceScope: "ALL" | "STANDARD";
  evidenceCompletenessPresent: boolean;
};

type EnabledStandardsResponse = {
  enabledStandards: string[];
  availableStandards: Array<{ code: string; name: string }>;
  operationalStandardCodes?: string[];
  scopedStandardCodes?: string[];
  userScopedStandardCodes?: string[];
  orchestrationEnabled?: boolean;
  metadata?: { starterGuide?: string[]; userScopedStandardCodes?: string[]; assessmentActivity?: string[] };
};

const emptyData: OverviewData = {
  overallCompliance: 0,
  verifiedScore: 0,
  verifiedAssessmentStatus: "NOT_READY",
  certificationStatus: "NOT_CERTIFIED",
  readinessStatus: "NOT_READY",
  auditReadinessScore: 0,
  activeStandards: 0,
  assessedStandards: 0,
  standardsWithCriticalGaps: 0,
  standardsReadyForAudit: 0,
  totalEvidence: 0,
  verifiedEvidence: 0,
  staleEvidence: 0,
  missingEvidence: 0,
  openCriticalRecommendations: 0,
  overdueRemediationTasks: 0,
  blockedControls: 0,
  plannedAudits: 0,
  remediationVelocity: 0,
  activeCertificationWorkflows: 0,
  packagesReady: 0,
  underAuditorReview: 0,
  openDriftEvents: 0,
  automationRulesEnabled: 0,
  failedAutomationRuns: 0,
  automatedEvidenceRequests: 0,
  aiProviderStatus: "DISABLED",
  recentAiRecommendations: 0,
  executiveRiskPosture: "Stable",
  topBlockers: 0,
  nextBestActions: 0,
  evidenceScope: "ALL",
  evidenceCompletenessPresent: false,
};

function toCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Append standardCode when a specific standard is selected (portfolio omits standardCode). */
function withStandard(url: string, standard: string) {
  return withComplianceScopeQuery(url, standard);
}

export default function ComplianceCenterOverview() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OverviewData>(emptyData);
  const [executiveIntelligence, setExecutiveIntelligence] = useState<ExecutiveSummaryData | null>(null);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [registrySaving, setRegistrySaving] = useState(false);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [showAllSupported, setShowAllSupported] = useState(false);
  const [registry, setRegistry] = useState<EnabledStandardsResponse>({ enabledStandards: [], availableStandards: [] });
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const [overviewStandard, setOverviewStandard] = useState(() => normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard")));
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1;
  const location = useLocation();
  const [effectiveAccess, setEffectiveAccess] = useState<EffectiveStandardAccess | null>(null);
  const [complianceTrendDelta, setComplianceTrendDelta] = useState(0);

  useEffect(() => {
    if (!token) return;
    const orgId = getActiveOrgId();
    if (!orgId) return;
    void fetchEffectiveStandardAccess(orgId)
      .then(setEffectiveAccess)
      .catch(() => setEffectiveAccess(null));
  }, [token]);

  useEffect(() => {
    if (!location.pathname.includes("overview")) return;
    const next = normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard"));
    setOverviewStandard((prev) => (prev === next ? prev : next));
  }, [location.pathname]);

  useEffect(() => {
    const n = normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard"));
    localStorage.setItem("ccSelectedStandard", n);
  }, []);

  useEffect(() => {
    const onStd = (e: Event) => {
      const d = (e as CustomEvent<string>).detail;
      const next = normalizeComplianceStandardCode(d ?? localStorage.getItem("ccSelectedStandard"));
      setOverviewStandard(next);
    };
    window.addEventListener("cc:selected-standard", onStd as EventListener);
    return () => window.removeEventListener("cc:selected-standard", onStd as EventListener);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setData(emptyData);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const scopedStandard = normalizeComplianceScope(overviewStandard);
        const std = overviewStandard;
        const responses = await Promise.allSettled([
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_SUMMARY, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS_GAPS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_RECOMMENDATIONS, std), { headers }),
          fetch(API_ENDPOINTS.GOVERNANCE_AUDITS, { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_REMEDIATION_TASKS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_AUTOMATION_RULES, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_AUTOMATION_RUNS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_DRIFT_EVENTS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_CERTIFICATION_WORKFLOWS, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.GOVERNANCE_EVIDENCE_STATUS_SUMMARY, std), { headers }),
          fetch(withStandard(API_ENDPOINTS.COMPLIANCE_AUTOMATED_EVIDENCE_REQUESTS, std), { headers }),
          fetch(
            scopedStandard
              ? `${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}?standardCode=${encodeURIComponent(scopedStandard)}&includeDiagnostics=true`
              : `${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}?includeDiagnostics=true`,
            { headers },
          ),
          featureFlags.AI_AUDIT_ASSISTANT_V1 ? fetch(withStandard(API_ENDPOINTS.AI_HEALTH, std), { headers }) : Promise.resolve(null as any),
        ]);
        const jsons = await Promise.all(
          responses.map(async (result) => {
            if (result.status !== "fulfilled" || !result.value || !("ok" in result.value) || !result.value.ok) return {};
            return result.value.json().catch(() => ({}));
          }),
        );
        const [
          complianceSummary,
          auditReadiness,
          readinessGaps,
          recommendations,
          audits,
          remediationTasks,
          automationRules,
          automationRuns,
          driftEvents,
          certificationWorkflows,
          evidenceSummary,
          evidenceRequests,
          executiveDashboard,
          aiHealth,
        ] = jsons as any[];

        let evidenceCompletenessSnapshot: {
          requirementTotal?: number;
          requirementCovered?: number;
          missingEvidence?: unknown[];
          staleEvidence?: unknown[];
          expiredEvidence?: unknown[];
        } | null = null;
        if (std && std !== "ALL") {
          const cr = await fetch(withStandard(API_ENDPOINTS.GOVERNANCE_EVIDENCE_COMPLETENESS, std), { headers });
          if (cr.ok) {
            const cj = await cr.json().catch(() => ({}));
            evidenceCompletenessSnapshot = (cj?.snapshot ?? null) as typeof evidenceCompletenessSnapshot;
          }
        }

        const workflows = Array.isArray(certificationWorkflows?.data) ? certificationWorkflows.data : [];
        const rules = Array.isArray(automationRules?.data) ? automationRules.data : [];
        const runs = Array.isArray(automationRuns?.data) ? automationRuns.data : [];
        const recs = Array.isArray(recommendations?.data) ? recommendations.data : [];
        const auditRows = Array.isArray(audits?.audits) ? audits.audits : [];
        const tasks = Array.isArray(remediationTasks?.data) ? remediationTasks.data : [];

        const cs = complianceSummary?.data ?? {};
        const cards = (cs as { cards?: { totalHealth?: number; activeStandards?: number; verifiedScore?: number } }).cards ?? {};
        const statusAuthority = (cs as { statusAuthority?: {
          selfAssessment?: { percent?: number };
          verifiedAssessment?: { percent?: number; status?: string };
          certification?: { status?: string };
          diagnostics?: { evidenceRepositorySummary?: Record<string, unknown> };
        } }).statusAuthority;
        const auditAuthority = auditReadiness?.data?.authority as typeof statusAuthority | undefined;
        const authority = statusAuthority || auditAuthority;
        const execSummary = (executiveDashboard?.data || {}) as Record<string, unknown>
        setExecutiveIntelligence((executiveDashboard?.data || null) as ExecutiveSummaryData | null);
        const overallCompliance = toCount(
          execSummary.overallCompliance ??
            execSummary.compliance?.overallCompliance ??
            execSummary.portfolioSummary?.portfolioReadiness ??
            0,
        );
        const activeStandardsCount = toCount(
          (cs as { activeStandardsCount?: number }).activeStandardsCount ?? cards.activeStandards,
        );
        const assessedStandardsCount = toCount((cs as { assessedStandardsCount?: number }).assessedStandardsCount);
        const auditReadyStandardsCount = toCount((cs as { auditReadyStandardsCount?: number }).auditReadyStandardsCount);

        const evRepo = authority?.diagnostics?.evidenceRepositorySummary as
          | {
              total?: number;
              verified?: number;
              stale?: number;
              missing?: number;
              totalArtifacts?: number;
              unverifiedArtifacts?: number;
              validArtifacts?: number;
              totalEvidence?: number;
              verifiedEvidence?: number;
              staleEvidence?: number;
              missingEvidence?: number;
            }
          | undefined;

        let totalEvidence = toCount(evidenceSummary?.data?.total);
        let verifiedEvidence = toCount(evidenceSummary?.data?.verified);
        let staleEvidence = toCount(evidenceSummary?.data?.stale);
        let missingEvidence = toCount(evidenceSummary?.data?.missing);
        let evidenceScope: "ALL" | "STANDARD" = "ALL";
        let evidenceCompletenessPresent = false;
        if (std && std !== "ALL" && evRepo) {
          evidenceScope = "STANDARD";
          evidenceCompletenessPresent = true;
          const artifactTotal = toCount(
            evRepo.totalArtifacts ?? evRepo.total ?? evRepo.totalEvidence,
          );
          const unverified = toCount(evRepo.unverifiedArtifacts ?? evRepo.missing ?? evRepo.missingEvidence);
          totalEvidence = artifactTotal;
          verifiedEvidence = Math.max(0, artifactTotal - unverified);
          staleEvidence = toCount(evRepo.stale ?? evRepo.staleEvidence);
          missingEvidence = unverified;
        } else if (std && std !== "ALL" && evidenceCompletenessSnapshot) {
          evidenceScope = "STANDARD";
          evidenceCompletenessPresent = true;
          const rt = Number(evidenceCompletenessSnapshot.requirementTotal ?? 0);
          const rc = Number(evidenceCompletenessSnapshot.requirementCovered ?? 0);
          totalEvidence = rt;
          verifiedEvidence = rc;
          staleEvidence = Array.isArray(evidenceCompletenessSnapshot.staleEvidence) ? evidenceCompletenessSnapshot.staleEvidence.length : 0;
          const miss = Array.isArray(evidenceCompletenessSnapshot.missingEvidence) ? evidenceCompletenessSnapshot.missingEvidence.length : 0;
          const exp = Array.isArray(evidenceCompletenessSnapshot.expiredEvidence) ? evidenceCompletenessSnapshot.expiredEvidence.length : 0;
          missingEvidence = miss + exp;
        } else if (std && std !== "ALL") {
          evidenceScope = "STANDARD";
          evidenceCompletenessPresent = false;
        }

        const certStatus = String(
          authority?.certification?.status ||
            auditReadiness?.data?.certificationStatus ||
            "NOT_CERTIFIED",
        );
        const verifiedStatus = String(
          authority?.verifiedAssessment?.status ||
            auditReadiness?.data?.verifiedAssessmentStatus ||
            auditReadiness?.data?.status ||
            "NOT_READY",
        );

        const remediationFromAuthority = authority?.diagnostics as
          | { overdueRemediationTasks?: number; openRemediationTasks?: number }
          | undefined;

        setData({
          overallCompliance,
          verifiedScore: toCount(
            execSummary.verifiedCompliance ??
              authority?.verifiedAssessment?.percent ??
              auditReadiness?.data?.verifiedScore ??
              cards.verifiedScore,
          ),
          verifiedAssessmentStatus: verifiedStatus,
          certificationStatus: certStatus,
          readinessStatus: verifiedStatus,
          auditReadinessScore: toCount(auditReadiness?.data?.readinessScore),
          activeStandards: activeStandardsCount,
          assessedStandards: assessedStandardsCount || activeStandardsCount,
          standardsWithCriticalGaps: toCount(readinessGaps?.data?.criticalCount ?? (Array.isArray(readinessGaps?.data) ? readinessGaps.data.length : 0)),
          standardsReadyForAudit: auditReadyStandardsCount,
          totalEvidence,
          verifiedEvidence,
          staleEvidence,
          missingEvidence,
          openCriticalRecommendations: toCount(
            execSummary.openRecommendations ??
              recs.filter((r: any) => String(r?.severity || "").toLowerCase() === "critical" && String(r?.status || "").toLowerCase() !== "completed").length,
          ),
          overdueRemediationTasks:
            std && std !== "ALL" && remediationFromAuthority?.overdueRemediationTasks != null
              ? toCount(remediationFromAuthority.overdueRemediationTasks)
              : tasks.filter((t: any) => Boolean(t?.isOverdue)).length,
          blockedControls: toCount(
            auditReadiness?.data?.blockedControls ??
              (authority?.diagnostics as { auditReadyBlockers?: unknown[] } | undefined)
                ?.auditReadyBlockers?.length,
          ),
          plannedAudits: auditRows.filter((item: any) => String(item?.status || "").toLowerCase() === "planned").length,
          remediationVelocity: toCount(recommendations?.analytics?.velocity ?? 0),
          activeCertificationWorkflows: workflows.length,
          packagesReady: workflows.filter((w: any) => String(w?.status || "").toLowerCase().includes("ready")).length,
          underAuditorReview: workflows.filter((w: any) => String(w?.status || "").toLowerCase().includes("review")).length,
          openDriftEvents: Array.isArray(driftEvents?.data) ? driftEvents.data.length : 0,
          automationRulesEnabled: rules.filter((r: any) => Boolean(r?.enabled)).length,
          failedAutomationRuns: runs.filter((r: any) => String(r?.status || "").toLowerCase() === "failed").length,
          automatedEvidenceRequests: Array.isArray(evidenceRequests?.data) ? evidenceRequests.data.length : 0,
          aiProviderStatus: String(aiHealth?.data?.status || (featureFlags.AI_AUDIT_ASSISTANT_V1 ? "UNKNOWN" : "DISABLED")),
          recentAiRecommendations: toCount(recs.filter((r: any) => Boolean(r?.aiGenerated)).length),
          executiveRiskPosture: String(execSummary.executivePosture || (toCount(executiveDashboard?.data?.openRisks) > 20 ? "Elevated" : "Stable")),
          topBlockers: toCount(executiveDashboard?.data?.openFindings),
          nextBestActions: Math.max(0, Math.min(5, toCount(executiveDashboard?.data?.overdueRemediation))),
          evidenceScope,
          evidenceCompletenessPresent,
        });
      } catch {
        setError(
          overviewStandard && overviewStandard !== "ALL"
            ? t("complianceCenterUi.overview.loadErrorForStandard", "Failed to load overview for {{standard}}.", {
                standard: getStandardDisplayName(t, overviewStandard),
              })
            : t("complianceCenterUi.overview.loadError", "Failed to load compliance center overview."),
        );
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [token, overviewStandard, t]);

  useEffect(() => {
    const loadRegistry = async () => {
      setRegistryLoading(true);
      setRegistryError(null);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED}?includeAll=${showAllSupported ? "1" : "0"}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json().catch(() => ({}));
        const payload = (json?.data || {}) as EnabledStandardsResponse;
        const nextEnabled = Array.isArray(payload.enabledStandards) ? payload.enabledStandards : [];
        const starterGuide = Array.isArray(payload.metadata?.starterGuide)
          ? payload.metadata.starterGuide
          : [];
        const userScoped = Array.isArray(payload.userScopedStandardCodes)
          ? payload.userScopedStandardCodes
          : Array.isArray(payload.metadata?.userScopedStandardCodes)
            ? payload.metadata.userScopedStandardCodes
            : [];
        const nextAvailable = Array.isArray(payload.availableStandards) ? payload.availableStandards : [];
        const scopedStandardCodes = Array.isArray(payload.scopedStandardCodes)
          ? payload.scopedStandardCodes.map((c) => normalizeComplianceStandardCode(c))
          : [];
        const operational = Array.isArray(payload.operationalStandardCodes)
          ? payload.operationalStandardCodes.map((c) => normalizeComplianceStandardCode(c))
          : [];
        setRegistry({
          enabledStandards: nextEnabled.map((c) => normalizeComplianceStandardCode(c)),
          availableStandards: nextAvailable,
          operationalStandardCodes: operational,
          scopedStandardCodes,
          userScopedStandardCodes: userScoped.map((c) => normalizeComplianceStandardCode(c)),
          metadata: payload.metadata,
        });
        setSelectedStandards(nextEnabled.map((c) => normalizeComplianceStandardCode(c)));
      } catch {
        setRegistryError(t("complianceCenterUi.standards.loadError", "Failed to load standards registry."));
      } finally {
        setRegistryLoading(false);
      }
    };
    void loadRegistry();
  }, [showAllSupported, t, token]);

  const saveEnabledStandards = async () => {
    setRegistrySaving(true);
    setRegistryError(null);
    try {
      await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ standards: selectedStandards }),
      });
      setRegistry((prev) => ({ ...prev, enabledStandards: selectedStandards }));
    } catch {
      setRegistryError(t("complianceCenterUi.standards.saveError", "Failed to save enabled standards."));
    } finally {
      setRegistrySaving(false);
    }
  };

  const execution = useComplianceExecution();
  const effectiveEnabledStandards = useMemo(() => {
    const set = new Set(registry.enabledStandards);
    if (featureFlags.COMPLIANCE_EXECUTION_ORCHESTRATION_V1) {
      for (const c of execution.enabledStandards) set.add(c);
    }
    return Array.from(set);
  }, [registry.enabledStandards, execution.enabledStandards]);

  const userScopedStandardCodes = useMemo(() => {
    const fromRegistry = registry.userScopedStandardCodes || registry.metadata?.userScopedStandardCodes || [];
    if (fromRegistry.length > 0) {
      return fromRegistry.map((c) => normalizeComplianceStandardCode(c)).filter(Boolean);
    }
    const fromStarter = registry.metadata?.starterGuide || [];
    return fromStarter.map((c) => normalizeComplianceStandardCode(c)).filter(Boolean);
  }, [registry.metadata?.starterGuide, registry.metadata?.userScopedStandardCodes, registry.userScopedStandardCodes]);

  const availableStandards = registry.availableStandards;
  const standardsForSelector = useMemo(() => {
    const seedEnabled = effectiveEnabledStandards.length > 0 ? effectiveEnabledStandards : registry.enabledStandards;
    const options = buildStandardsSelectorOptions(
      seedEnabled,
      availableStandards,
      effectiveAccess,
      userScopedStandardCodes,
    );
    const dedup = new Map<string, { code: string; name: string }>();
    for (const item of options) {
      if (item.code && item.code !== "ALL") {
        dedup.set(item.code, { code: item.code, name: item.name || item.code });
      }
    }
    return [
      {
        code: "ALL",
        name: t("complianceCenterUi.standards.displayNames.ALL", "ALL standards"),
      },
      ...Array.from(dedup.values()),
    ];
  }, [
    availableStandards,
    effectiveEnabledStandards,
    registry.enabledStandards,
    effectiveAccess,
    userScopedStandardCodes,
    t,
  ]);

  const hasNoStandards = useMemo(() => {
    const selectable = standardsForSelector.filter((item) => item.code !== "ALL");
    return selectable.length === 0 && data.activeStandards === 0;
  }, [standardsForSelector, data.activeStandards]);

  const hasNoEvidence = useMemo(() => {
    if (overviewStandard === "ALL") {
      return data.evidenceScope === "ALL" && data.totalEvidence === 0;
    }
    if (data.evidenceScope !== "STANDARD") return true;
    if (!data.evidenceCompletenessPresent) return true;
    return data.verifiedEvidence === 0;
  }, [data.evidenceCompletenessPresent, data.evidenceScope, data.totalEvidence, data.verifiedEvidence, overviewStandard]);
  const hasNoRemediation = data.overdueRemediationTasks === 0 && data.openCriticalRecommendations === 0;
  const hasNoCertification = data.activeCertificationWorkflows === 0;
  const aiDisabled = !featureFlags.AI_AUDIT_ASSISTANT_V1 || data.aiProviderStatus === "DISABLED";

  const verifiedSubtitle = useMemo(() => {
    const s = String(data.verifiedAssessmentStatus || "").toUpperCase();
    if (s === "AUDIT_READY") return t("complianceStatusAuthority.verified.auditReadyLabel", "Verified / Audit-ready");
    if (s === "PARTIALLY_VERIFIED") return t("complianceStatusAuthority.verified.partiallyVerified", "Partially verified");
    return t("complianceStatusAuthority.verified.notAuditReady", "Not audit-ready");
  }, [data.verifiedAssessmentStatus, t]);

  const certificationSubtitle = useMemo(() => {
    const s = String(data.certificationStatus || "").toUpperCase();
    if (s === "CERTIFIED") return t("complianceStatusAuthority.certification.certified", "Certified");
    if (s === "UNDER_EXTERNAL_REVIEW") return t("complianceStatusAuthority.certification.underExternalReview", "Under external review");
    if (s === "CERTIFICATION_EXPIRED") return t("complianceStatusAuthority.certification.expired", "Certification expired");
    return t("complianceStatusAuthority.certification.notCertified", "Not certified");
  }, [data.certificationStatus, t]);

  const metricCards = useMemo(
    () => [
      {
        title: t("complianceCenterUi.metrics.overall", "Overall Compliance"),
        value: `${data.overallCompliance}%`,
        subtitle: t("complianceStatusAuthority.selfAssessed", "Self-assessed"),
      },
      {
        title: t("complianceCenterUi.metrics.verified", "Verified Compliance"),
        value: `${data.verifiedScore}%`,
        subtitle: verifiedSubtitle,
      },
      {
        title: t("complianceCenterUi.metrics.evidence", "Evidence Health"),
        value: `${data.verifiedEvidence}/${data.totalEvidence}`,
        subtitle: `${t("complianceCenterUi.metrics.stale", "Stale")}: ${data.staleEvidence} · ${t("complianceCenterUi.metrics.missing", "Missing")}: ${data.missingEvidence}`,
      },
      {
        title: t("complianceCenterUi.metrics.remediation", "Remediation Health"),
        value: String(data.openCriticalRecommendations),
        subtitle: `${t("complianceCenterUi.metrics.overdueTasks", "Overdue tasks")}: ${data.overdueRemediationTasks}`,
      },
      {
        title: t("complianceCenterUi.metrics.certification", "Certification Status"),
        value: certificationSubtitle,
        subtitle: t("complianceStatusAuthority.certification.externalOnly", "External certification only"),
      },
      {
        title: t("complianceCenterUi.metrics.internalAuditsAndItControls", "Internal audits & IT controls"),
        value: String(data.plannedAudits),
        subtitle: t("complianceCenterUi.metrics.plannedAuditsItControls", "Planned internal audits and IT control reviews"),
      },
      {
        title: t("complianceCenterUi.metrics.automation", "Automation & Drift"),
        value: String(data.openDriftEvents),
        subtitle: `${t("complianceCenterUi.metrics.rulesEnabled", "Rules enabled")}: ${data.automationRulesEnabled}`,
      },
      {
        title: t("complianceCenterUi.metrics.ai", "AI Assistant"),
        value: data.aiProviderStatus,
        subtitle: `${t("complianceCenterUi.metrics.aiRecommendations", "Recent AI recommendations")}: ${data.recentAiRecommendations}`,
      },
    ],
    [data, t, verifiedSubtitle, certificationSubtitle],
  );

  const signalCards = useMemo(() => {
    const filtered = overviewStandard !== "ALL";
    const stdName = filtered ? getStandardDisplayName(t, overviewStandard) : "";

    const evidenceCard = (() => {
      if (hasNoStandards) {
        return {
          key: "evidence",
          title: t("complianceCenterUi.overview.signalEvidenceTitle", "Evidence"),
          body: t(
            "complianceCenterUi.emptyStates.noStandardsBody",
            "Complete Starter Guide or enable standards to start operational compliance workflows.",
          ),
        };
      }
      if (hasNoEvidence) {
        return {
          key: "evidence",
          title: filtered
            ? t("complianceCenterUi.emptyStates.noEvidenceTitleFiltered", "No evidence linked for {{standardName}} yet", { standardName: stdName })
            : t("complianceCenterUi.emptyStates.noEvidenceTitle", "No evidence yet"),
          body: filtered
            ? t(
                "complianceCenterUi.emptyStates.noEvidenceBodyFiltered",
                "Upload or link evidence that supports {{standardName}} controls to improve readiness confidence.",
                { standardName: stdName },
              )
            : t(
                "complianceCenterUi.emptyStates.noEvidenceBody",
                "Upload evidence in Evidence Hub to improve verification and readiness confidence.",
              ),
        };
      }
      return {
        key: "evidence",
        title: filtered
          ? `${t("complianceCenterUi.overview.signalEvidenceTitle", "Evidence")} (${stdName})`
          : t("complianceCenterUi.overview.signalEvidenceTitle", "Evidence"),
        body: filtered
          ? t("complianceCenterUi.overview.signalEvidenceBodyActiveFiltered", "Evidence for {{standardName}}: verified {{verified}} / {{total}} (stale {{stale}}, missing {{missing}}).", {
              standardName: stdName,
              verified: data.verifiedEvidence,
              total: data.totalEvidence,
              stale: data.staleEvidence,
              missing: data.missingEvidence,
            })
          : t("complianceCenterUi.overview.signalEvidenceBodyActive", "Verified {{verified}} / {{total}} evidence records (stale {{stale}}, missing {{missing}}).", {
              verified: data.verifiedEvidence,
              total: data.totalEvidence,
              stale: data.staleEvidence,
              missing: data.missingEvidence,
            }),
      };
    })();

    const remediationCard = (() => {
      if (hasNoStandards) {
        return {
          key: "remediation",
          title: t("complianceCenterUi.overview.signalRemediationTitle", "Remediation"),
          body: t(
            "complianceCenterUi.emptyStates.noStandardsBody",
            "Complete Starter Guide or enable standards to start operational compliance workflows.",
          ),
        };
      }
      if (hasNoRemediation) {
        return {
          key: "remediation",
          title: filtered
            ? t("complianceCenterUi.emptyStates.noRemediationTitleFiltered", "No remediation tasks for {{standardName}}", { standardName: stdName })
            : t("complianceCenterUi.emptyStates.noRemediationTitle", "No remediation tasks"),
          body: filtered
            ? t("complianceCenterUi.emptyStates.noRemediationBodyFiltered", "When {{standardName}} controls are partial, failed, or missing evidence, remediation tasks will appear here.", {
                standardName: stdName,
              })
            : t(
                "complianceCenterUi.emptyStates.noRemediationBody",
                "When partial or failed controls appear, remediation tasks and ownership tracking will show here.",
              ),
        };
      }
      return {
        key: "remediation",
        title: filtered
          ? `${t("complianceCenterUi.overview.signalRemediationTitle", "Remediation")} (${stdName})`
          : t("complianceCenterUi.overview.signalRemediationTitle", "Remediation"),
        body: filtered
          ? t(
              "complianceCenterUi.overview.signalRemediationBodyActiveFiltered",
              "{{standardName}}: {{critical}} critical recommendations open; {{overdue}} overdue remediation tasks.",
              { standardName: stdName, critical: data.openCriticalRecommendations, overdue: data.overdueRemediationTasks },
            )
          : t("complianceCenterUi.overview.signalRemediationBodyActive", "{{critical}} critical recommendations open; {{overdue}} overdue remediation tasks.", {
              critical: data.openCriticalRecommendations,
              overdue: data.overdueRemediationTasks,
            }),
      };
    })();

    const aiCard = (() => {
      if (hasNoStandards) {
        return {
          key: "ai",
          title: t("complianceCenterUi.overview.signalAiTitle", "AI assistant"),
          body: t(
            "complianceCenterUi.emptyStates.noStandardsBody",
            "Complete Starter Guide or enable standards to start operational compliance workflows.",
          ),
        };
      }
      if (aiDisabled) {
        const baseBody = filtered
          ? t(
              "complianceCenterUi.emptyStates.aiDisabledBodyFiltered",
              "AI disabled for {{standardName}}. AI assistance remains manual and disabled by default until feature flags and provider setup are enabled.",
              { standardName: stdName },
            )
          : t(
              "complianceCenterUi.emptyStates.aiDisabledBody",
              "AI remains manual and disabled by default until feature flags and provider setup are enabled.",
            );
        return {
          key: "ai",
          title: filtered
            ? t("complianceCenterUi.emptyStates.aiDisabledTitleFiltered", "AI disabled for {{standardName}}", { standardName: stdName })
            : t("complianceCenterUi.emptyStates.aiDisabledTitle", "AI disabled"),
          body: (
            <>
              {baseBody}{" "}
              <Link className={enterpriseUx ? entStyles.linkAction : styles.signalInlineLink} to="/integrations">
                {t("complianceCenterUi.overview.aiSetupIntegrationsLink", "Open Integrations to configure providers.")}
              </Link>{" "}
              {t(
                "complianceCenterUi.overview.aiFeatureFlagHint",
                "If this build ships with AI off, set VITE_AI_AUDIT_ASSISTANT_V1=true and redeploy.",
              )}
            </>
          ),
        };
      }
      return {
        key: "ai",
        title: filtered
          ? `${t("complianceCenterUi.overview.signalAiTitle", "AI assistant")} (${stdName})`
          : t("complianceCenterUi.overview.signalAiTitle", "AI assistant"),
        body: filtered
          ? t("complianceCenterUi.overview.signalAiBodyActiveFiltered", "{{standardName}} — provider status: {{status}}; recent AI-tagged recommendations: {{count}}.", {
              standardName: stdName,
              status: data.aiProviderStatus,
              count: data.recentAiRecommendations,
            })
          : t("complianceCenterUi.overview.signalAiBodyActive", "Provider status: {{status}}; recent AI-tagged recommendations: {{count}}.", {
              status: data.aiProviderStatus,
              count: data.recentAiRecommendations,
            }),
      };
    })();

    const certCard = (() => {
      if (hasNoStandards) {
        return {
          key: "certification",
          title: t("complianceCenterUi.overview.signalCertificationTitle", "Certification"),
          body: t(
            "complianceCenterUi.emptyStates.noStandardsBody",
            "Complete Starter Guide or enable standards to start operational compliance workflows.",
          ),
        };
      }
      if (hasNoCertification) {
        const certBody = filtered
          ? t(
              "complianceCenterUi.emptyStates.noCertificationBodyFiltered",
              "Create a {{standardName}} certification workflow to track package readiness and auditor review lifecycle.",
              { standardName: stdName },
            )
          : t(
              "complianceCenterUi.emptyStates.noCertificationBody",
              "Create a certification workflow to track package readiness and auditor review lifecycle.",
            );
        return {
          key: "certification",
          title: filtered
            ? t("complianceCenterUi.emptyStates.noCertificationTitleFiltered", "Certification not started for {{standardName}}", { standardName: stdName })
            : t("complianceCenterUi.emptyStates.noCertificationTitle", "Certification not started"),
          body: (
            <>
              {certBody}{" "}
              {featureFlags.CERTIFICATION_WORKFLOW_V1 ? (
                <Link className={enterpriseUx ? entStyles.linkAction : styles.signalInlineLink} to="/compliance-center/certification">
                  {t("complianceCenterUi.overview.openCertificationWorkspace", "Open the Certification workspace.")}
                </Link>
              ) : (
                <span className={styles.signalBodyMuted}>
                  {t(
                    "complianceCenterUi.overview.certificationWorkflowFlagOff",
                    "Certification workflows are off in this deployment (VITE_CERTIFICATION_WORKFLOW_V1).",
                  )}
                </span>
              )}
            </>
          ),
        };
      }
      return {
        key: "certification",
        title: filtered
          ? `${t("complianceCenterUi.overview.signalCertificationTitle", "Certification")} (${stdName})`
          : t("complianceCenterUi.overview.signalCertificationTitle", "Certification"),
        body: filtered
          ? t(
              "complianceCenterUi.overview.signalCertBodyActiveFiltered",
              "{{standardName}}: {{active}} active workflows ({{ready}} ready, {{review}} under review).",
              {
                standardName: stdName,
                active: data.activeCertificationWorkflows,
                ready: data.packagesReady,
                review: data.underAuditorReview,
              },
            )
          : t("complianceCenterUi.overview.signalCertBodyActive", "{{active}} active workflows ({{ready}} ready, {{review}} under review).", {
              active: data.activeCertificationWorkflows,
              ready: data.packagesReady,
              review: data.underAuditorReview,
            }),
      };
    })();

    return [evidenceCard, remediationCard, aiCard, certCard];
  }, [
    aiDisabled,
    data.activeCertificationWorkflows,
    data.aiProviderStatus,
    data.missingEvidence,
    data.openCriticalRecommendations,
    data.overdueRemediationTasks,
    data.packagesReady,
    data.recentAiRecommendations,
    data.staleEvidence,
    data.totalEvidence,
    data.underAuditorReview,
    data.verifiedEvidence,
    hasNoCertification,
    hasNoEvidence,
    hasNoRemediation,
    hasNoStandards,
    overviewStandard,
    t,
    featureFlags.CERTIFICATION_WORKFLOW_V1,
    enterpriseUx,
  ]);
  useEffect(() => {
    if (registryLoading) return;
    const codes = standardsForSelector
      .map((item) => normalizeComplianceStandardCode(item.code))
      .filter((c) => c !== "ALL");
    if (!codes.length) return;
    const current = normalizeComplianceStandardCode(overviewStandard);
    if (current !== "ALL" && codes.includes(current)) return;
    if (codes.length === 1) {
      const only = codes[0];
      setOverviewStandard(only);
      localStorage.setItem("ccSelectedStandard", only);
      window.dispatchEvent(new CustomEvent("cc:selected-standard", { detail: only }));
    }
  }, [registryLoading, standardsForSelector, overviewStandard]);

  const executiveOverviewStandards = useMemo(() => {
    return standardsForSelector
      .filter((item) => normalizeComplianceStandardCode(item.code) !== "ALL")
      .filter((item) => {
        const code = normalizeComplianceStandardCode(item.code);
        if (featureFlags.STANDARD_ACCESS_POLICY_V1 && effectiveAccess) {
          return isStandardGrantedInEffective(effectiveAccess, code);
        }
        return true;
      })
      .map((item) => normalizeComplianceStandardCode(item.code));
  }, [standardsForSelector, effectiveAccess]);

  const standardCardCodes = useMemo(() => {
    if (overviewStandard && overviewStandard !== "ALL") {
      const code = normalizeComplianceStandardCode(overviewStandard);
      return code ? [code] : [];
    }
    return executiveOverviewStandards;
  }, [overviewStandard, executiveOverviewStandards]);

  useEffect(() => {
    if (!token) {
      setComplianceTrendDelta(0);
      return;
    }
    const codes =
      overviewStandard && overviewStandard !== "ALL"
        ? [normalizeComplianceStandardCode(overviewStandard)]
        : executiveOverviewStandards;
    void (async () => {
      try {
        const res = await metricsApi.getFrameworkMetrics();
        const series = Array.isArray(res?.data) ? res.data : [];
        const filtered =
          codes.length > 0
            ? series.filter((row) =>
                codes.some((code) =>
                  frameworkMatchesCode(row.framework, code, getStandardDisplayName(t, code)),
                ),
              )
            : series;
        const byDate = new Map<string, Record<string, number | string>>();
        for (const row of filtered) {
          for (const pt of row.data || []) {
            const date = String(pt?.date ?? "");
            if (!date) continue;
            const bucket = byDate.get(date) || { date };
            bucket[row.framework] = Number(pt?.value ?? 0);
            byDate.set(date, bucket);
          }
        }
        const rows = Array.from(byDate.values()).sort((a, b) =>
          String(a.date).localeCompare(String(b.date)),
        );
        if (rows.length < 2) {
          setComplianceTrendDelta(0);
          return;
        }
        const trend = rows.map((row) => {
          const keys = Object.keys(row).filter(
            (k) => k !== "date" && typeof row[k] === "number",
          );
          const avg = keys.length
            ? keys.reduce((sum, k) => sum + Number(row[k] || 0), 0) / keys.length
            : 0;
          return avg;
        });
        const last = trend[trend.length - 1] ?? 0;
        const first = trend[0] ?? last;
        setComplianceTrendDelta(Math.round(last - first));
      } catch {
        setComplianceTrendDelta(0);
      }
    })();
  }, [token, overviewStandard, executiveOverviewStandards, t]);

  const standardsSelectorOptions = useMemo(
    () =>
      standardsForSelector
        .filter((item) => {
          const optCode = normalizeComplianceStandardCode(item.code);
          if (optCode === "ALL") return true;
          if (
            featureFlags.STANDARD_ACCESS_POLICY_V1 &&
            effectiveAccess?.standardCodes?.length
          ) {
            return isStandardGrantedInEffective(effectiveAccess, optCode);
          }
          return true;
        })
        .map((item) => {
          const optCode = normalizeComplianceStandardCode(item.code);
          return {
            value: optCode,
            label:
              optCode === "ALL"
                ? getStandardDisplayName(t, "ALL")
                : item.name || getStandardDisplayName(t, optCode),
          };
        }),
    [standardsForSelector, effectiveAccess, t],
  );

  const onOverviewStandardChange = (nextRaw: string | null) => {
    const next = normalizeComplianceStandardCode(nextRaw ?? "ALL");
    setData(emptyData);
    setError(null);
    setOverviewStandard(next);
    localStorage.setItem("ccSelectedStandard", next === "ALL" ? "ALL" : next);
    window.dispatchEvent(new CustomEvent("cc:selected-standard", { detail: next }));
  };

  const standardsScopePanel = (
    <>
      {featureFlags.STANDARD_ACCESS_POLICY_V1 && effectiveAccess && effectiveAccess.standardCodes.length === 0 ? (
        <div className={enterpriseUx ? entStyles.alertBanner : styles.noStandardsBanner} role="status">
          <strong>{t(NO_STANDARDS_GRANTED_MESSAGE_KEY, "No standards granted for this organization or user.")}</strong>
        </div>
      ) : null}
      {hasNoStandards ? (
        <div className={enterpriseUx ? entStyles.alertBanner : styles.noStandardsBanner} role="status">
          <strong>{t("complianceCenterUi.emptyStates.noStandardsTitle", "No standards selected")}</strong>
          <p className={enterpriseUx ? entStyles.postureSubtitle : styles.noStandardsBannerText}>
            {t("complianceCenterUi.emptyStates.noStandardsBody", "Complete Starter Guide or enable standards to start operational compliance workflows.")}
          </p>
        </div>
      ) : null}
      {enterpriseUx ? (
        <div className={entStyles.formStack}>
          <Select<string>
            label={t("complianceCenterUi.standards.selectorLabel", "Standards selector")}
            minWidth="100%"
            variant="outline"
            options={standardsSelectorOptions}
            value={overviewStandard}
            onChange={onOverviewStandardChange}
            disabled={registryLoading}
          />
          {overviewStandard !== "ALL" ? (
            <div className={entStyles.formActions}>
              <OperationalWorkflowButton standardCode={overviewStandard} effectiveAccess={effectiveAccess} />
              {featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={() => navigate(executiveGovernanceStandardPath(overviewStandard))}
                >
                  {t("executiveGovernance.executiveDrilldown", "Executive drill-down")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.standardControls}>
          <div className={styles.standardRow}>
            <label htmlFor="overview-standard-selector" className={styles.standardLabel}>
              {t("complianceCenterUi.standards.selectorLabel", "Standards selector")}
            </label>
            <select
              id="overview-standard-selector"
              className={styles.standardSelect}
              value={overviewStandard}
              onChange={(event) => onOverviewStandardChange(event.target.value)}
              disabled={registryLoading}
            >
              {standardsForSelector.map((item) => {
                const optCode = normalizeComplianceStandardCode(item.code);
                const notGranted =
                  optCode !== "ALL" &&
                  featureFlags.STANDARD_ACCESS_POLICY_V1 &&
                  effectiveAccess &&
                  !isStandardGrantedInEffective(effectiveAccess, optCode);
                return (
                  <option key={optCode} value={optCode} disabled={notGranted}>
                    {optCode === "ALL"
                      ? getStandardDisplayName(t, "ALL")
                      : `${item.name || getStandardDisplayName(t, optCode)}${notGranted ? ` (${t("standardGovernance.notGranted")})` : ""}`}
                  </option>
                );
              })}
            </select>
            {overviewStandard !== "ALL" ? (
              <>
                <OperationalWorkflowButton standardCode={overviewStandard} effectiveAccess={effectiveAccess} />
                {featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    onClick={() => navigate(executiveGovernanceStandardPath(overviewStandard))}
                  >
                    {t("executiveGovernance.executiveDrilldown", "Executive drill-down")}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      )}
      <p className={enterpriseUx ? entStyles.postureSubtitle : styles.selectorHelper}>
        {overviewStandard === "ALL"
          ? t("complianceCenterUi.overview.selectorHelperAll", "Showing signals across all standards.")
          : t("complianceCenterUi.overview.selectorHelperSpecific", "Showing signals for {{standardName}}.", {
              standardName: getStandardDisplayName(t, overviewStandard),
            })}
      </p>
      {loading ? (
        <p className={enterpriseUx ? entStyles.postureSubtitle : styles.signalsUpdating} aria-live="polite">
          {t("complianceCenterUi.overview.updatingSignals", "Updating signals…")}
        </p>
      ) : null}
      {isAdmin ? (
        <div className={enterpriseUx ? entStyles.formStack : styles.standardAdmin}>
          <label className={enterpriseUx ? entStyles.checkboxRow : styles.adminCheckbox}>
            <input type="checkbox" checked={showAllSupported} onChange={(event) => setShowAllSupported(event.target.checked)} />
            {t("complianceCenterUi.standards.showAll", "Show all supported standards")}
          </label>
          <div className={enterpriseUx ? entStyles.checkboxGrid : styles.adminPills}>
            {availableStandards.map((item) => {
              const checked = selectedStandards.includes(item.code);
              const notGranted =
                featureFlags.STANDARD_ACCESS_POLICY_V1 &&
                effectiveAccess &&
                !isStandardGrantedInEffective(effectiveAccess, item.code);
              return (
                <label
                  key={item.code}
                  className={enterpriseUx ? entStyles.checkboxLabel : styles.adminPill}
                  title={notGranted ? t("standardGovernance.notGranted") : undefined}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!!notGranted}
                    onChange={(event) => {
                      setSelectedStandards((prev) =>
                        event.target.checked ? [...prev, item.code] : prev.filter((code) => code !== item.code),
                      );
                    }}
                  />
                  {item.name || item.code}
                  {notGranted ? " *" : ""}
                </label>
              );
            })}
          </div>
          {enterpriseUx ? (
            <Button type="button" size="small" color="outline" onClick={() => void saveEnabledStandards()} disabled={registrySaving}>
              {registrySaving
                ? t("complianceCenterUi.standards.saving", "Saving...")
                : t("complianceCenterUi.standards.save", "Save enabled standards")}
            </Button>
          ) : (
            <button type="button" className={styles.saveButton} onClick={() => void saveEnabledStandards()} disabled={registrySaving}>
              {registrySaving
                ? t("complianceCenterUi.standards.saving", "Saving...")
                : t("complianceCenterUi.standards.save", "Save enabled standards")}
            </button>
          )}
        </div>
      ) : null}
      {registryError ? (
        <p className={enterpriseUx ? entStyles.registryError : styles.registryError} role="alert">
          {registryError}
        </p>
      ) : null}
    </>
  );

  const metricsPanel = (
    <div className={`${enterpriseUx ? entStyles.standardGrid : styles.metricsGrid} ${loading ? styles.metricsDimmed : ""}`}>
      {metricCards.map((card) =>
        enterpriseUx ? (
          <EnterpriseOperationalCard key={card.title} title={card.title}>
            <p className={entStyles.postureMetricValue}>{card.value}</p>
            <p className={entStyles.postureSubtitle}>{card.subtitle}</p>
          </EnterpriseOperationalCard>
        ) : (
          <article key={card.title} className={styles.metricCard}>
            <h2 className={styles.metricTitle}>{card.title}</h2>
            <div className={styles.metricValue}>{card.value}</div>
            <p className={styles.metricSubtitle}>{card.subtitle}</p>
          </article>
        ),
      )}
    </div>
  );

  const signalsPanel = (
    <>
      <p className={enterpriseUx ? entStyles.postureSubtitle : styles.signalsSubtitle}>
        {t("complianceCenterUi.emptyStates.subtitle", "The platform highlights missing setup so teams can start from the highest-impact actions.")}
        {overviewStandard && overviewStandard !== "ALL" ? (
          <span className={enterpriseUx ? entStyles.scopeLine : styles.standardFilterHint}>
            {" "}
            {t("complianceCenterUi.emptyStates.standardFilterHint", "(Metrics and signals reflect {{standardName}}.)", {
              standardName: getStandardDisplayName(t, overviewStandard),
            })}
          </span>
        ) : null}
      </p>
      <div className={enterpriseUx ? entStyles.standardGrid : styles.signalsGrid}>
        {signalCards.map((signal) =>
          enterpriseUx ? (
            <EnterpriseOperationalCard key={signal.key} title={signal.title}>
              <div className={entStyles.postureSubtitle}>{signal.body}</div>
            </EnterpriseOperationalCard>
          ) : (
            <article key={signal.key} className={styles.signalCard}>
              <h4 className={styles.signalTitle}>{signal.title}</h4>
              <p className={styles.signalBody}>{signal.body}</p>
            </article>
          ),
        )}
      </div>
    </>
  );

  const selfAssessmentBlock = enterpriseUx ? (
    <EnterpriseOperationalSection
      layerLabel={t("complianceCenterUi.selfAssessment.layerLabel", "Self-assessment")}
      title={t("complianceCenterUi.selfAssessment.title", "Self-Assessment & questionnaires")}
      subtitle={t(
        "complianceCenterUi.selfAssessment.subtitle",
        "Answer control questions and review maturity charts. Results feed Home and the compliance metrics above.",
      )}
      testId="cc-overview-self-assessment-enterprise"
    >
      <details className={entStyles.legacyDetails}>
        <summary className={entStyles.legacyDetailsSummary}>
          {t(
            "complianceCenterUi.selfAssessment.expand",
            "Questionnaires, radar charts & control sections",
          )}
        </summary>
        <div className={entStyles.embeddedCompliance}>
          <Compliance
            embeddedMode
            enterpriseEmbedded
            selectedStandard={overviewStandard}
          />
        </div>
      </details>
    </EnterpriseOperationalSection>
  ) : (
    <details className={styles.legacyComplianceDetails}>
      <summary className={styles.legacyComplianceSummary}>
        {t(
          "complianceCenterUi.selfAssessment.expand",
          "Questionnaires, radar charts & control sections",
        )}
      </summary>
      <div className="mt-3">
        <Compliance embeddedMode selectedStandard={overviewStandard} />
      </div>
    </details>
  );

  const overviewBody = (
    <>
      {error ? (
        <div className={enterpriseUx ? entStyles.alertBanner : styles.metricCard} role="alert">
          {error}
        </div>
      ) : null}

      {enterpriseUx ? (
        <EnterpriseOperationalSection
          layerLabel={t("complianceCenterUi.overview.scopeLayer", "Compliance scope")}
          title={t("complianceCenterUi.standards.scopeTitle", "Standards & scope")}
          subtitle={t("complianceCenterUi.overview.scopeHint", "Select the standard lens for metrics, signals, and operational workflows.")}
          testId="cc-overview-scope-enterprise"
        >
          {standardsScopePanel}
        </EnterpriseOperationalSection>
      ) : (
        <section className={styles.signalsSection}>{standardsScopePanel}</section>
      )}

      {standardCardCodes.length > 0 ? (
        enterpriseUx ? (
          <EnterpriseOperationalSection
            layerLabel={t("enterpriseOperationalUx.layer2and3", "Certification stability & standards")}
            title={t("enterpriseOperationalUx.standardExecutiveView", "Standard executive view")}
            subtitle={t(
              "complianceCenterUi.standardCards.hint",
              "Per-standard compliance posture, operational pressure, and workflow entry points.",
            )}
            testId="cc-overview-standard-cards-enterprise"
          >
            <ComplianceCenterStandardExecutiveCards
              standardCodes={standardCardCodes}
              effectiveAccess={effectiveAccess}
            />
          </EnterpriseOperationalSection>
        ) : (
          <section className={styles.signalsSection} data-testid="cc-overview-standard-cards">
            <h3 className={styles.signalsTitle}>
              {t("enterpriseOperationalUx.standardExecutiveView", "Standard executive view")}
            </h3>
            <ComplianceCenterStandardExecutiveCards
              standardCodes={standardCardCodes}
              effectiveAccess={effectiveAccess}
            />
          </section>
        )
      ) : null}

      {enterpriseUx ? (
        <EnterpriseOperationalSection
          layerLabel={t("complianceCenterUi.metrics.layerLabel", "Posture metrics")}
          title={t("complianceCenterUi.metrics.workspaceTitle", "Compliance posture metrics")}
          subtitle={t(
            "complianceCenterUi.metrics.workspaceHint",
            "Self-assessed and verified compliance, evidence health, remediation, and certification signals.",
          )}
          testId="cc-overview-metrics-enterprise"
        >
          {metricsPanel}
        </EnterpriseOperationalSection>
      ) : (
        <section className={styles.signalsSection}>
          <h3 className={styles.signalsTitle}>{t("complianceCenterUi.metrics.workspaceTitle", "Compliance posture metrics")}</h3>
          <p className={styles.signalsSubtitle}>
            {t(
              "complianceCenterUi.metrics.workspaceHint",
              "Self-assessed and verified compliance, evidence health, remediation, and certification signals.",
            )}
          </p>
          {metricsPanel}
        </section>
      )}

      {enterpriseUx ? (
        <EnterpriseOperationalSection
          layerLabel={t("enterpriseOperationalUx.operationalLayer", "Operational compliance")}
          title={t("enterpriseOperationalUx.operationalComplianceWorkspace", "Operational Compliance Workspace")}
          subtitle={t(
            "enterpriseOperationalUx.ccWorkspaceHint",
            "Executive pressure, runtime signals, and certification stability — compliance percentages remain in metrics above.",
          )}
          testId="cc-overview-operational-enterprise"
        >
          <EnterpriseOperationalOverviewPanel overviewStandard={overviewStandard} />
        </EnterpriseOperationalSection>
      ) : null}

      <HomeExecutiveGovernanceIntelligence
        data={executiveIntelligence}
        variant="compliance"
        className={enterpriseUx ? entStyles.layer : styles.signalsSection}
        complianceTrendDelta={complianceTrendDelta}
      />

      {featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
        <ExecutiveStandardsOverviewBlock standardCodes={executiveOverviewStandards} enterpriseUx={enterpriseUx} />
      ) : null}

      {enterpriseUx ? (
        <EnterpriseOperationalSection
          layerLabel={t("complianceCenterUi.emptyStates.layerLabel", "Readiness signals")}
          title={t("complianceCenterUi.emptyStates.title", "Operational Readiness Signals")}
          testId="cc-overview-signals-enterprise"
        >
          {signalsPanel}
        </EnterpriseOperationalSection>
      ) : (
        <section className={styles.signalsSection}>
          <h3 className={styles.signalsTitle}>{t("complianceCenterUi.emptyStates.title", "Operational Readiness Signals")}</h3>
          {signalsPanel}
        </section>
      )}

      {selfAssessmentBlock}
    </>
  );

  return (
    <div className={styles.root}>
      {enterpriseUx ? <EnterpriseOperationalPage>{overviewBody}</EnterpriseOperationalPage> : overviewBody}
    </div>
  );
}
